import React from "react";

import Link from "next/link";

import { Button } from "@heroui/button";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { OrganizationTerminology } from "@/lib/services/terminologyService";

interface EmptyStateProps {
  title?: string;
  message?: string;
  actionLabel?: string;
  actionHref?: string;
  /**
   * Optional ID for the component
   */
  id?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Accessible label for screen readers
   */
  ariaLabel: string;
  /**
   * Custom aria-label for the action button
   */
  actionAriaLabel: string;
  /**
   * Type of empty state to show different messages
   */
  variant?: "filtered" | "no-data" | "error";
  /**
   * Organization name for personalized messaging
   */
  organizationName?: string;
  /**
   * Terminology for language customization (required; use getDefaultTerminology() if no org).
   */
  terminology: OrganizationTerminology;
}

function getEmptyStateContent(
  variant: "filtered" | "no-data" | "error",
  terminology: OrganizationTerminology,
  organizationName?: string,
) {
  const needPlural = terminology.need.plural;
  const volunteerPlural = terminology.volunteer.plural;
  const termCommunity = terminology.termCommunity;

  switch (variant) {
    case "no-data":
      return {
        actionLabel: "Browse Other Organizations",
        icon: "heart",
        message: organizationName
          ? `${organizationName} doesn't have any active ${needPlural.toLowerCase()} for ${volunteerPlural.toLowerCase()} right now. Check back soon – this ${termCommunity.toLowerCase()} values your willingness to help!`
          : `This organization doesn't have any active ${needPlural.toLowerCase()} for ${volunteerPlural.toLowerCase()} right now. Check back soon!`,
        title: organizationName
          ? `${organizationName} has no active ${needPlural.toLowerCase()} right now`
          : `No active ${needPlural.toLowerCase()} right now`,
      };
    case "error":
      return {
        actionLabel: "Try Again",
        icon: "alert",
        message: `We're having trouble loading the ${needPlural.toLowerCase()} right now. Please try again in a moment.`,
        title: `Unable to load ${needPlural.toLowerCase()}`,
      };
    case "filtered":
    default:
      return {
        actionLabel: "Clear Filters",
        icon: "search",
        message: `All ${needPlural.toLowerCase()} in the area have currently been met! Thank you for your willingness to help. Check back soon or try adjusting your filters to see if there are ${needPlural.toLowerCase()} in other areas.`,
        title: `All ${needPlural.toLowerCase()} have been met`,
      };
  }
}

/**
 * Empty state component displayed when no needs are found
 *
 * Accessibility features:
 * - Semantic section element with proper role
 * - Descriptive aria-label
 * - Icon with proper aria-hidden attribute
 * - Proper heading structure
 * - Accessible button with clear purpose
 */
function resolveVariant(
  variant: EmptyStateProps["variant"],
): "filtered" | "no-data" | "error" {
  return variant ?? "filtered";
}

function resolveActionLabel(
  actionLabel: string | undefined,
  contentActionLabel: string,
): string {
  return actionLabel ?? contentActionLabel;
}

function resolveButtonAriaLabel(
  actionAriaLabel: string,
  actionLabel: string | undefined,
  contentActionLabel: string,
): string {
  return actionAriaLabel ?? resolveActionLabel(actionLabel, contentActionLabel);
}

function resolveEmptyStateProps(props: EmptyStateProps) {
  const variant = resolveVariant(props.variant);
  const content = getEmptyStateContent(
    variant,
    props.terminology,
    props.organizationName,
  );
  const finalActionLabel = resolveActionLabel(
    props.actionLabel,
    content.actionLabel,
  );

  return {
    actionHref: props.actionHref ?? "/",
    buttonAriaLabel: resolveButtonAriaLabel(
      props.actionAriaLabel,
      props.actionLabel,
      content.actionLabel,
    ),
    className: props.className ?? "",
    componentId: props.id ?? "empty-state",
    finalActionLabel,
    finalMessage: props.message ?? content.message,
    finalTitle: props.title ?? content.title,
    variant,
  };
}

export function EmptyState(props: EmptyStateProps) {
  const {
    actionHref,
    buttonAriaLabel,
    className,
    componentId,
    finalActionLabel,
    finalMessage,
    finalTitle,
    variant,
  } = resolveEmptyStateProps(props);

  const titleId = `${componentId}-title`;
  const messageId = `${componentId}-message`;

  return (
    <section
      aria-describedby={messageId}
      aria-labelledby={titleId}
      className={`col-span-full flex flex-col items-center justify-center py-16 px-4 ${className}`}
      id={componentId}
      role="status"
    >
      <div
        aria-hidden="true"
        className="bg-default-50 w-16 h-16 rounded-full flex items-center justify-center mb-4"
      >
        <svg
          aria-hidden="true"
          className="w-8 h-8 text-default-400"
          fill="none"
          focusable="false"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      </div>
      <h3 className="text-xl font-medium text-default-800 mb-2" id={titleId}>
        {finalTitle}
      </h3>
      <p className="text-default-500 text-center max-w-md mb-6" id={messageId}>
        {finalMessage}
      </p>
      {actionHref && (
        <Link href={actionHref}>
          <Button
            aria-label={buttonAriaLabel}
            color="primary"
            variant="flat"
            onPress={() => {
              void EventPublisher.publishEvent(
                EventKeys.EMPTY_STATE_ACTION_BUTTON_CLICK,
                {
                  actionHref,
                  actionLabel: finalActionLabel,
                  variant,
                },
              );
            }}
          >
            {finalActionLabel}
          </Button>
        </Link>
      )}
    </section>
  );
}
